import { BaseDocument } from "./base";
import { UserId } from "./helpers";
import { createPartial, createPartialExcluding, createWithoutUnderscoreFields } from "./helpers/partial";


export enum EProjectStatus {
    DRAFT, // draft project - created by employer but not available for platform
    WAITING_FOR_PM, // waiting for pm - need active subscription
    NOT_STARTED,  // pm assigned but not yet started
    NOT_STARTED_APPROVAL_REQUESTED, // pm requested approval of project/tasks details by employer
    NOT_STARTED_APPROVAL_REJECTED, // approval rejected - need rework
    IN_PROGRESS, // sb has started working on a task
    IN_PROGRESS_REASSIGN_PM, // searching a new pm 
    COMPLETION_REQUESTED, // pm requested project completion
    COMPLETION_REQUEST_REJECTED, // pm request for completion requested (like in progress)
    COMPLETED, // all tasks completed
    CANCELLED, // pm cancelled project
    PAUSED, // pm paused project
    ARCHIVED, // employer archived project
}

export enum EProjectActivityType {
    UPLOADED_FILE,
    COMMENT_CREATED,
    PROJECT_MANAGER_ASSIGNED,
    PROJECT_MANAGER_REASSIGNED,
    TASK_CREATED,
    TASK_ASSIGNED,
    TASK_REASSIGNED,
    TASK_COMPLETED,
}

export enum EProjectPaymentType {
    NOT_SET,
    ONE_TIME_FULL,
    TASK_BY_TASK,
}

export interface UploadedFile {
    id: string,
    url: string,
    name: string,
    type: string,
    size: number,
    uploaderId: string,
}

export interface ProjectReview {
    id: string,
    reviewerId: string,
    reviewerUserType: string,
    rating: number,
    reviewText: string,
}

export interface ProjectActivity {
    id: string,
    type: EProjectActivityType,
    createdAt: Date,
    title: string,
    description: string,
}

export interface AssignedFreelancer {
    freelancerId: string,
    assignedByManager: UserId,
    assignedAt: Date,
    unassigneAt: Date | null,
    taskId: string,
}

export interface FreelancerApplication {
    id: string,
    freelancerId: string,
    freelancerUserType: string,
}

export interface Project extends BaseDocument {
    _techMetadata: any,
    _publishedAt: Date | null,
    _completedAt: Date | null,
    _cancelledAt: Date | null,
    _approvalRequestedAt: Date | null,
    _selfManagedStartedAt: Date | null,
    _startedWithPmAt: Date | null,
    _rejectedApprovalRequestAt: Date | null,
    _detailsApprovedAt: Date | null,
    _completionRequestedAt: Date | null,
    _rejectedCompletionRequestAt: Date | null,

    _approvalRejectedReasons: string[],
    _numberRejectedApprovals: number,
    _completionRejectedReasons: string[],
    _numberRejectedCompletions: number,

    _waitingForRoadmapVerification: boolean,
    _waitingForRoadmapVerificationAt: Date | null,
    _roadmapVerifyTriggeredBy: UserId | null,
    _roadmapVerifiedBy: string | null,
    _roadmapVerifiedAt: Date | null,
    _roadmapPmAgreedToContinue: boolean,
    _roadmapPmAgreedToContinueAt: Date | null,

    employerId: string,
    status: EProjectStatus,
    paymentType: EProjectPaymentType,
    fileFolderUrl: string,
    requireNda: boolean,
    ndaId: string,
    markedAsCompleteEmployer: boolean,
    markedAsCompletePm: boolean,
    markedAsCompleteAdmin: boolean,

    title: string, // title of the project
    subtitle: string, // subtitle of the project
    briefDescription: string // brief description of the project (public info)
    description: string, // description of the project
    expectedDelivery: string, // what is expected to be delivered (figma design, c++ code in github)
    requiredTools: string[], // tools that should be known by freelancer
    requiredSkills: string[], // skills that should be known by freelancer
    fileUrls: string[], // urls of files needed for description or full information
    urls: string[], // urls where work should be done (e.g. figma)
    risks: string, // risks of the project

    ownerManaged: boolean, // if employer is managing the project
    currentProjectManager: string | null, // freelancer id
    currentRoadmapVerificationPm: string | null, // freelancer id
    unassignedProjectManagers: string[], // freelancer ids
    unassignedRoadmapVerificationPms: string[], // freelancer ids
    startDate: Date | null,
    dueDate: Date | null,
    totalCredits: number,

    tasks: string[] // task ids
    completedTasks: string[] // task ids
    currentPmCompletedTasks: string[] // task ids
    percentageCompleted: number // percentage of completed tasks

    assignedFreelancers: AssignedFreelancer[] // freelancer ids
    unassignedFreelancers: AssignedFreelancer[] // freelancers that where unassigned from the project
    uploadedFileUrls: string[], // file urls

    comments: string[], // comment ids
    reviews: ProjectReview[], // review ids
}


export const createDefaultProject = (): Project => ({
    _techMetadata: null,
    _publishedAt: null,
    _completedAt: null,
    _cancelledAt: null,
    _approvalRequestedAt: null,
    _selfManagedStartedAt: null,
    _startedWithPmAt: null,
    _rejectedApprovalRequestAt: null,
    _detailsApprovedAt: null,
    _completionRequestedAt: null,
    _rejectedCompletionRequestAt: null,

    _approvalRejectedReasons: [],
    _numberRejectedApprovals: 0,
    _completionRejectedReasons: [],
    _numberRejectedCompletions: 0,

    _waitingForRoadmapVerification: false,
    _waitingForRoadmapVerificationAt: null,
    _roadmapVerifyTriggeredBy: null,
    _roadmapVerifiedBy: null,
    _roadmapVerifiedAt: null,
    _roadmapPmAgreedToContinue: true,
    _roadmapPmAgreedToContinueAt: null,

    employerId: '',
    status: EProjectStatus.DRAFT,
    paymentType: EProjectPaymentType.ONE_TIME_FULL,
    fileFolderUrl: '',
    requireNda: false,
    ndaId: '',
    markedAsCompleteEmployer: false,
    markedAsCompletePm: false,
    markedAsCompleteAdmin: false,

    title: '',
    subtitle: '',
    briefDescription: '',
    description: '',
    expectedDelivery: '',
    requiredTools: [],
    requiredSkills: [],
    risks: '',

    fileUrls: [],
    urls: [],

    ownerManaged: false,
    currentProjectManager: '',
    currentRoadmapVerificationPm: '',
    unassignedProjectManagers: [],
    unassignedRoadmapVerificationPms: [],
    startDate: null,
    dueDate: null,
    totalCredits: 0,

    tasks: [],
    completedTasks: [],
    currentPmCompletedTasks: [],
    percentageCompleted: 0,

    assignedFreelancers: [],
    unassignedFreelancers: [],
    uploadedFileUrls: [],

    comments: [],
    reviews: [],
});

export function projectBrief(project: IProject): IProject {
    const fields = [
        '_id',
        'title',
        'subtitle',
        'startDate',
        'dueDate',
        'status',
        'briefDescription',
        'percentageCompleted',
    ]
    return createPartial(project, fields);
}
export interface IProject extends Partial<Project> { };